import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui';
import { Book, Terminal, ChevronDown, ChevronRight } from 'lucide-react';

interface ApiEndpoint {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  path: string;
  description: string;
  auth: boolean;
  scope: 'read' | 'write' | 'admin';
  params?: string;
  body?: string;
  response?: string;
}

interface ApiCategory {
  name: string;
  icon: string;
  endpoints: ApiEndpoint[];
}

const API_CATEGORIES: ApiCategory[] = [
  {
    name: '认证',
    icon: '🔐',
    endpoints: [
      { method: 'POST', path: '/api/v1/auth/login', description: '用户登录', auth: false, scope: 'read', body: '{ "username": "string", "password": "string" }', response: '{ "token": "string", "user": {...} }' },
      { method: 'POST', path: '/api/v1/auth/register', description: '用户注册', auth: false, scope: 'read', body: '{ "username": "string", "email": "string", "password": "string" }' },
      { method: 'POST', path: '/api/v1/auth/logout', description: '用户登出', auth: true, scope: 'read' },
      { method: 'GET', path: '/api/v1/auth/me', description: '获取当前用户信息', auth: true, scope: 'read' },
      { method: 'PUT', path: '/api/v1/auth/profile', description: '更新个人资料', auth: true, scope: 'write', body: '{ "display_name"?: "string", "avatar_url"?: "string" }' },
      { method: 'PUT', path: '/api/v1/auth/password', description: '修改密码', auth: true, scope: 'write', body: '{ "old_password": "string", "new_password": "string" }' },
    ],
  },
  {
    name: '页面',
    icon: '📄',
    endpoints: [
      { method: 'GET', path: '/api/v1/pages', description: '获取页面列表', auth: false, scope: 'read', params: '?page=1&perPage=20&tag=xxx&visibility=public' },
      { method: 'POST', path: '/api/v1/pages', description: '创建页面', auth: true, scope: 'write', body: '{ "title": "string", "content": "string", "visibility"?: "public", "tags"?: [], "space_id"?: number }' },
      { method: 'GET', path: '/api/v1/pages/:slug', description: '获取页面详情', auth: false, scope: 'read' },
      { method: 'PUT', path: '/api/v1/pages/:slug', description: '更新页面', auth: true, scope: 'write', body: '{ "title"?: "string", "content"?: "string", "tags"?: [] }' },
      { method: 'DELETE', path: '/api/v1/pages/:slug', description: '删除页面', auth: true, scope: 'write' },
      { method: 'GET', path: '/api/v1/pages/:slug/versions', description: '获取页面版本历史', auth: true, scope: 'read' },
      { method: 'POST', path: '/api/v1/pages/:slug/versions/:version/restore', description: '恢复到指定版本', auth: true, scope: 'write' },
      { method: 'GET', path: '/api/v1/pages/:slug/export', description: '导出页面为 Markdown', auth: false, scope: 'read' },
      { method: 'GET', path: '/api/v1/pages/:slug/children', description: '获取子页面列表', auth: false, scope: 'read' },
      { method: 'GET', path: '/api/v1/pages/:slug/backlinks', description: '获取反向链接', auth: false, scope: 'read' },
      { method: 'POST', path: '/api/v1/pages/:slug/favorite', description: '收藏页面', auth: true, scope: 'write' },
      { method: 'DELETE', path: '/api/v1/pages/:slug/favorite', description: '取消收藏', auth: true, scope: 'write' },
      { method: 'POST', path: '/api/v1/pages/:slug/subscribe', description: '订阅页面更新', auth: true, scope: 'write' },
      { method: 'DELETE', path: '/api/v1/pages/:slug/subscribe', description: '取消订阅', auth: true, scope: 'write' },
      { method: 'POST', path: '/api/v1/pages/:slug/copy', description: '复制页面', auth: true, scope: 'write' },
      { method: 'GET', path: '/api/v1/pages/templates', description: '获取模板列表', auth: false, scope: 'read' },
    ],
  },
  {
    name: '空间',
    icon: '📁',
    endpoints: [
      { method: 'GET', path: '/api/v1/spaces', description: '获取空间列表', auth: true, scope: 'read', params: '?page=1&perPage=20' },
      { method: 'POST', path: '/api/v1/spaces', description: '创建空间', auth: true, scope: 'write', body: '{ "name": "string", "description"?: "string" }' },
      { method: 'GET', path: '/api/v1/spaces/:slug', description: '获取空间详情', auth: true, scope: 'read' },
      { method: 'PATCH', path: '/api/v1/spaces/:slug', description: '更新空间', auth: true, scope: 'write', body: '{ "name"?: "string", "description"?: "string" }' },
      { method: 'DELETE', path: '/api/v1/spaces/:slug', description: '删除空间', auth: true, scope: 'write' },
      { method: 'GET', path: '/api/v1/spaces/:slug/members', description: '获取空间成员列表', auth: true, scope: 'read' },
      { method: 'POST', path: '/api/v1/spaces/:slug/members', description: '添加空间成员', auth: true, scope: 'write', body: '{ "user_id": number, "role": "admin|member|viewer" }' },
      { method: 'DELETE', path: '/api/v1/spaces/:slug/members/:userId', description: '移除空间成员', auth: true, scope: 'write' },
      { method: 'PATCH', path: '/api/v1/spaces/:slug/members/:userId/role', description: '更新成员角色', auth: true, scope: 'write', body: '{ "role": "admin|member|viewer" }' },
      { method: 'GET', path: '/api/v1/spaces/:slug/pages', description: '获取空间内的页面', auth: true, scope: 'read' },
    ],
  },
  {
    name: '搜索',
    icon: '🔍',
    endpoints: [
      { method: 'GET', path: '/api/v1/search', description: '搜索页面内容', auth: false, scope: 'read', params: '?q=keyword&page=1&perPage=20' },
    ],
  },
  {
    name: '标签',
    icon: '🏷️',
    endpoints: [
      { method: 'GET', path: '/api/v1/tags', description: '获取所有标签', auth: false, scope: 'read' },
    ],
  },
  {
    name: '评论',
    icon: '💬',
    endpoints: [
      { method: 'GET', path: '/api/v1/pages/:slug/comments', description: '获取页面评论', auth: false, scope: 'read' },
      { method: 'POST', path: '/api/v1/pages/:slug/comments', description: '添加评论', auth: true, scope: 'write', body: '{ "content": "string", "parent_id"?: number }' },
      { method: 'DELETE', path: '/api/v1/pages/:slug/comments/:id', description: '删除评论', auth: true, scope: 'write' },
    ],
  },
  {
    name: '附件',
    icon: '📎',
    endpoints: [
      { method: 'GET', path: '/api/v1/attachments', description: '获取附件列表', auth: true, scope: 'read', params: '?page=1&perPage=20' },
      { method: 'POST', path: '/api/v1/attachments/upload', description: '上传附件', auth: true, scope: 'write', body: 'multipart/form-data' },
      { method: 'GET', path: '/api/v1/attachments/:id', description: '获取附件详情', auth: true, scope: 'read' },
      { method: 'DELETE', path: '/api/v1/attachments/:id', description: '删除附件', auth: true, scope: 'write' },
    ],
  },
  {
    name: '用户',
    icon: '👤',
    endpoints: [
      { method: 'GET', path: '/api/v1/users', description: '获取用户列表', auth: true, scope: 'read', params: '?page=1&perPage=20' },
      { method: 'GET', path: '/api/v1/users/:id', description: '获取用户详情', auth: true, scope: 'read' },
    ],
  },
  {
    name: '通知',
    icon: '🔔',
    endpoints: [
      { method: 'GET', path: '/api/v1/notifications', description: '获取通知列表', auth: true, scope: 'read', params: '?page=1&perPage=20&unread_only=true' },
      { method: 'POST', path: '/api/v1/notifications/:id/read', description: '标记通知已读', auth: true, scope: 'write' },
      { method: 'POST', path: '/api/v1/notifications/read-all', description: '标记全部已读', auth: true, scope: 'write' },
      { method: 'GET', path: '/api/v1/notifications/unread-count', description: '获取未读数量', auth: true, scope: 'read' },
    ],
  },
  {
    name: 'Webhooks',
    icon: '🔗',
    endpoints: [
      { method: 'GET', path: '/api/v1/webhooks', description: '获取 Webhook 列表', auth: true, scope: 'read' },
      { method: 'POST', path: '/api/v1/webhooks', description: '创建 Webhook', auth: true, scope: 'write', body: '{ "url": "string", "events": ["page.created", "page.updated"], "secret"?: "string" }' },
      { method: 'DELETE', path: '/api/v1/webhooks/:id', description: '删除 Webhook', auth: true, scope: 'write' },
      { method: 'POST', path: '/api/v1/webhooks/:id/test', description: '测试 Webhook', auth: true, scope: 'write' },
    ],
  },
  {
    name: 'API Keys',
    icon: '🔑',
    endpoints: [
      { method: 'GET', path: '/api/v1/api-keys', description: '获取 API Key 列表', auth: true, scope: 'read' },
      { method: 'GET', path: '/api/v1/api-keys/enhanced', description: '获取增强版 API Key 列表', auth: true, scope: 'read' },
      { method: 'POST', path: '/api/v1/api-keys', description: '创建 API Key', auth: true, scope: 'write', body: '{ "name": "string", "scopes": "read|write|admin", "space_ids"?: [] }' },
      { method: 'GET', path: '/api/v1/api-keys/:id', description: '获取 API Key 详情', auth: true, scope: 'read' },
      { method: 'GET', path: '/api/v1/api-keys/:id/stats', description: '获取 API Key 使用统计', auth: true, scope: 'read' },
      { method: 'GET', path: '/api/v1/api-keys/:id/logs', description: '获取 API Key 请求日志', auth: true, scope: 'read' },
      { method: 'PATCH', path: '/api/v1/api-keys/:id', description: '更新 API Key', auth: true, scope: 'write', body: '{ "description"?: "string", "space_ids"?: [] }' },
      { method: 'DELETE', path: '/api/v1/api-keys/:id', description: '吊销 API Key', auth: true, scope: 'write' },
    ],
  },
  {
    name: '管理 (仅管理员)',
    icon: '⚙️',
    endpoints: [
      { method: 'GET', path: '/api/v1/admin/users', description: '获取所有用户', auth: true, scope: 'admin', params: '?page=1&perPage=20' },
      { method: 'PATCH', path: '/api/v1/admin/users/:id', description: '更新用户角色', auth: true, scope: 'admin', body: '{ "role": "admin|editor|reader" }' },
      { method: 'DELETE', path: '/api/v1/admin/users/:id', description: '删除用户', auth: true, scope: 'admin' },
      { method: 'GET', path: '/api/v1/admin/audit-logs', description: '获取审计日志', auth: true, scope: 'admin', params: '?page=1&perPage=50' },
      { method: 'GET', path: '/api/v1/admin/settings', description: '获取系统设置', auth: true, scope: 'admin' },
      { method: 'PUT', path: '/api/v1/admin/settings', description: '更新系统设置', auth: true, scope: 'admin', body: '{ "site_name"?: "string", "allow_registration"?: boolean }' },
      { method: 'GET', path: '/api/v1/admin/api-keys', description: '获取所有 API Keys', auth: true, scope: 'admin', params: '?page=1&perPage=20' },
      { method: 'DELETE', path: '/api/v1/admin/api-keys/:id', description: '吊销任意 API Key', auth: true, scope: 'admin' },
    ],
  },
  {
    name: '分析统计',
    icon: '📊',
    endpoints: [
      { method: 'GET', path: '/api/v1/analytics/pages', description: '获取页面访问统计', auth: true, scope: 'read', params: '?days=7' },
      { method: 'GET', path: '/api/v1/analytics/popular', description: '获取热门页面', auth: true, scope: 'read', params: '?limit=10' },
    ],
  },
];

export function ApiDocs() {
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [expandedEndpoint, setExpandedEndpoint] = useState<string | null>(null);

  const getMethodColor = (method: string) => {
    const colors: Record<string, string> = {
      GET: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      POST: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      PUT: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      PATCH: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
      DELETE: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    };
    return colors[method] || 'bg-gray-100 text-gray-800';
  };

  const getScopeLabel = (scope: string) => {
    const labels: Record<string, string> = {
      read: '只读',
      write: '读写',
      admin: '管理员',
    };
    return labels[scope] || scope;
  };

  const getScopeColor = (scope: string) => {
    const colors: Record<string, string> = {
      read: 'text-green-600 dark:text-green-400',
      write: 'text-blue-600 dark:text-blue-400',
      admin: 'text-red-600 dark:text-red-400',
    };
    return colors[scope] || '';
  };

  return (
    <div className="max-w-5xl mx-auto py-8 space-y-6">
      {/* Header */}
      <div className="text-center space-y-2">
        <h1 className="text-3xl font-bold flex items-center justify-center gap-3">
          <Book className="h-8 w-8" />
          API 文档
        </h1>
        <p className="text-muted-foreground">
          Wiki REST API 完整参考文档，支持通过 API Key 或用户认证访问
        </p>
      </div>

      {/* Quick start */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Terminal className="h-5 w-5" />
            快速开始
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <h4 className="font-medium mb-2">认证方式</h4>
              <p className="text-sm text-muted-foreground mb-2">
                在请求头中添加 Authorization:
              </p>
              <pre className="bg-muted p-3 rounded text-sm overflow-x-auto">
{`Authorization: Bearer ww_your_api_key`}
              </pre>
            </div>
            <div>
              <h4 className="font-medium mb-2">基础 URL</h4>
              <p className="text-sm text-muted-foreground mb-2">
                所有 API 请求的基础地址:
              </p>
              <pre className="bg-muted p-3 rounded text-sm overflow-x-auto">
{`https://your-wiki.com/api/v1`}
              </pre>
            </div>
          </div>

          <div>
            <h4 className="font-medium mb-2">示例请求</h4>
            <pre className="bg-muted p-3 rounded text-sm overflow-x-auto">
{`# 获取页面列表
curl -X GET "https://your-wiki.com/api/v1/pages" \\
  -H "Authorization: Bearer ww_your_api_key" \\
  -H "Content-Type: application/json"

# 创建新页面
curl -X POST "https://your-wiki.com/api/v1/pages" \\
  -H "Authorization: Bearer ww_your_api_key" \\
  -H "Content-Type: application/json" \\
  -d '{"title": "新页面", "content": "页面内容"}'`}
            </pre>
          </div>
        </CardContent>
      </Card>

      {/* Response format */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">响应格式</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <h4 className="font-medium mb-2 text-green-600">成功响应</h4>
              <pre className="bg-muted p-3 rounded text-sm overflow-x-auto">
{`{
  "success": true,
  "data": { ... },
  "error": null
}`}
              </pre>
            </div>
            <div>
              <h4 className="font-medium mb-2 text-red-600">错误响应</h4>
              <pre className="bg-muted p-3 rounded text-sm overflow-x-auto">
{`{
  "success": false,
  "data": null,
  "error": {
    "code": "ERROR_CODE",
    "message": "错误描述"
  }
}`}
              </pre>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* API Categories */}
      <div className="space-y-2">
        {API_CATEGORIES.map((category) => (
          <Card key={category.name}>
            <button
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-muted/50 transition-colors rounded-lg"
              onClick={() => setExpandedCategory(expandedCategory === category.name ? null : category.name)}
            >
              <span className="flex items-center gap-2 font-medium">
                <span className="text-xl">{category.icon}</span>
                {category.name}
                <span className="text-xs text-muted-foreground font-normal">
                  ({category.endpoints.length} 个端点)
                </span>
              </span>
              {expandedCategory === category.name ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </button>

            {expandedCategory === category.name && (
              <div className="border-t divide-y">
                {category.endpoints.map((endpoint, index) => (
                  <div key={`${endpoint.method}-${endpoint.path}`}>
                    <button
                      className="w-full px-4 py-2 flex items-center gap-3 hover:bg-muted/30 transition-colors text-left"
                      onClick={() => setExpandedEndpoint(
                        expandedEndpoint === `${category.name}-${index}` ? null : `${category.name}-${index}`
                      )}
                    >
                      <span className={`px-2 py-0.5 text-xs font-medium rounded ${getMethodColor(endpoint.method)}`}>
                        {endpoint.method}
                      </span>
                      <code className="flex-1 text-sm font-mono">{endpoint.path}</code>
                      <span className="text-sm text-muted-foreground hidden md:block">{endpoint.description}</span>
                      {endpoint.auth && (
                        <span className="text-xs text-muted-foreground" title="需要认证">🔐</span>
                      )}
                    </button>

                    {expandedEndpoint === `${category.name}-${index}` && (
                      <div className="px-4 py-3 bg-muted/30 text-sm space-y-2">
                        <div className="flex items-start gap-2">
                          <span className="text-muted-foreground min-w-[80px]">描述:</span>
                          <span>{endpoint.description}</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="text-muted-foreground min-w-[80px]">权限要求:</span>
                          <span className={getScopeColor(endpoint.scope)}>
                            {getScopeLabel(endpoint.scope)}
                            {endpoint.auth && ' (需要认证)'}
                          </span>
                        </div>
                        {endpoint.params && (
                          <div className="flex items-start gap-2">
                            <span className="text-muted-foreground min-w-[80px]">查询参数:</span>
                            <code className="text-xs bg-muted px-2 py-0.5 rounded break-all">{endpoint.params}</code>
                          </div>
                        )}
                        {endpoint.body && (
                          <div className="flex items-start gap-2">
                            <span className="text-muted-foreground min-w-[80px]">请求体:</span>
                            <pre className="text-xs bg-muted px-2 py-1 rounded overflow-x-auto flex-1">
                              {endpoint.body}
                            </pre>
                          </div>
                        )}
                        {endpoint.response && (
                          <div className="flex items-start gap-2">
                            <span className="text-muted-foreground min-w-[80px]">响应示例:</span>
                            <pre className="text-xs bg-muted px-2 py-1 rounded overflow-x-auto flex-1">
                              {endpoint.response}
                            </pre>
                          </div>
                        )}
                        <div className="flex items-start gap-2">
                          <span className="text-muted-foreground min-w-[80px]">示例请求:</span>
                          <pre className="text-xs bg-muted px-2 py-1 rounded overflow-x-auto flex-1">
{`curl -X ${endpoint.method} "https://your-wiki.com${endpoint.path}${endpoint.params || ''}" \\
  -H "Authorization: Bearer ww_your_api_key" \\
  -H "Content-Type: application/json"${endpoint.body ? ` \\
  -d '${endpoint.body}'` : ''}`}
                          </pre>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
        ))}
      </div>

      {/* Scope legend */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">权限范围说明</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="flex items-start gap-3 p-3 border rounded-lg">
              <span className="w-4 h-4 rounded-full bg-green-500 mt-0.5"></span>
              <div>
                <h4 className="font-medium">只读 (read)</h4>
                <p className="text-sm text-muted-foreground">只能读取数据，不能进行任何修改操作</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 border rounded-lg">
              <span className="w-4 h-4 rounded-full bg-blue-500 mt-0.5"></span>
              <div>
                <h4 className="font-medium">读写 (write)</h4>
                <p className="text-sm text-muted-foreground">可以创建、编辑和删除内容</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 border rounded-lg">
              <span className="w-4 h-4 rounded-full bg-red-500 mt-0.5"></span>
              <div>
                <h4 className="font-medium">管理员 (admin)</h4>
                <p className="text-sm text-muted-foreground">完全访问权限，包括用户管理和系统设置</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error codes */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">常见错误码</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-3">HTTP 状态码</th>
                  <th className="text-left py-2 px-3">错误码</th>
                  <th className="text-left py-2 px-3">说明</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <td className="py-2 px-3">400</td>
                  <td className="py-2 px-3 font-mono text-xs">VALIDATION_ERROR</td>
                  <td className="py-2 px-3">请求参数验证失败</td>
                </tr>
                <tr className="border-b">
                  <td className="py-2 px-3">401</td>
                  <td className="py-2 px-3 font-mono text-xs">UNAUTHORIZED</td>
                  <td className="py-2 px-3">未认证，需要登录或提供有效的 API Key</td>
                </tr>
                <tr className="border-b">
                  <td className="py-2 px-3">403</td>
                  <td className="py-2 px-3 font-mono text-xs">FORBIDDEN</td>
                  <td className="py-2 px-3">无权限访问该资源</td>
                </tr>
                <tr className="border-b">
                  <td className="py-2 px-3">404</td>
                  <td className="py-2 px-3 font-mono text-xs">NOT_FOUND</td>
                  <td className="py-2 px-3">请求的资源不存在</td>
                </tr>
                <tr className="border-b">
                  <td className="py-2 px-3">409</td>
                  <td className="py-2 px-3 font-mono text-xs">CONFLICT</td>
                  <td className="py-2 px-3">资源冲突（如用户名已存在）</td>
                </tr>
                <tr className="border-b">
                  <td className="py-2 px-3">429</td>
                  <td className="py-2 px-3 font-mono text-xs">RATE_LIMITED</td>
                  <td className="py-2 px-3">请求频率超限</td>
                </tr>
                <tr>
                  <td className="py-2 px-3">500</td>
                  <td className="py-2 px-3 font-mono text-xs">INTERNAL_ERROR</td>
                  <td className="py-2 px-3">服务器内部错误</td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default ApiDocs;
